export interface BinomialName {
  valid: boolean;
  genus: string | null;
  species: string | null;
  normalized: string;
  errors: string[];
}

/** Validates the two-word, capitalized-genus/lowercase-specific-epithet convention. */
export function validateBinomialName(input: string): BinomialName {
  const normalized = input.trim().replace(/\s+/g, ' ').replace(/^<i>(.*)<\/i>$/i, '$1').replace(/^_(.*)_$/, '$1');
  const words = normalized.split(' ');
  const errors: string[] = [];
  if (words.length !== 2) errors.push('A binomial name contains exactly two words: Genus and species epithet.');
  const genus = words[0] || null;
  const species = words[1] || null;
  if (genus && !/^[A-Z][a-z-]+$/.test(genus)) errors.push('The genus begins with a capital letter; remaining letters are lowercase.');
  if (species && !/^[a-z][a-z-]+$/.test(species)) errors.push('The specific epithet begins with a lowercase letter.');
  return { valid: errors.length === 0, genus, species, normalized, errors };
}

export function matchesScientificName(input: string, expected: string): boolean {
  const submitted = validateBinomialName(input);
  const reference = validateBinomialName(expected);
  return submitted.valid && reference.valid && submitted.genus === reference.genus && submitted.species === reference.species;
}

export function formatScientificName(input: string, format: 'html' | 'markdown' | 'plain' = 'html'): string {
  const parsed = validateBinomialName(input);
  if (!parsed.valid || !parsed.genus || !parsed.species) throw new RangeError(parsed.errors.join(' '));
  if (format === 'markdown') return `*${parsed.genus} ${parsed.species}*`;
  if (format === 'plain') return `${parsed.genus} ${parsed.species}`;
  return `<i>${parsed.genus}</i> <i>${parsed.species}</i>`;
}
